import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './SeoPage.css';

/**
 * Programmatic SEO landing pages — one component, many routes, each
 * targeting a specific long-tail keyword. Content is 100% static and
 * server-rendered on first paint (via react-snap or Vercel ISR in future).
 */

const PAGES = {
  'fortnite-tournaments': {
    title: 'Fortnite Tournaments for Real Money — $1 Entry | EZGAME',
    h1:    'Fortnite Tournaments for Real Money',
    description: 'Enter Fortnite kill tournaments for $1 USDC. Play one match, win 90% of the prize pool paid instantly on Polygon. Gas under $0.01. No qualifiers.',
    game:  'fortnite',
    body: `
      <p>EZGAME runs the simplest cash Fortnite tournaments on the internet. $1 USDC entry, one match, 30-minute window. The player with the most eliminations wins 90% of the total prize pool — paid instantly to your Polygon wallet.</p>
      <p>No signup forms. No banking info. No region lock. Connect MetaMask, enter your Epic username, and play. If you win, USDC hits your wallet in under 10 seconds after the match is verified.</p>
    `,
    cta: { text: 'Enter a Fortnite Tournament →', to: '/fortnite' },
  },
  'apex-legends-tournaments': {
    title: 'Apex Legends Tournaments for Cash — $1 USDC Entry | EZGAME',
    h1:    'Apex Legends Cash Tournaments',
    description: 'Compete in Apex Legends kill tournaments for $1 USDC. Most kills in a single ranked match wins 90% of the prize pool. Instant payout on Polygon.',
    game:  'apex',
    body: `
      <p>Drop into a ranked Apex match and earn real money. EZGAME's Apex tournaments cost $1 to enter, run for 30 minutes, and pay 90% of the prize pool to the player with the most kills.</p>
      <p>Payouts are instant — the smart contract sends USDC to your wallet the moment the match is verified via Tracker.gg. No withdrawal queue. No minimum threshold.</p>
    `,
    cta: { text: 'Enter an Apex Tournament →', to: '/lobby' },
  },
  'crypto-gaming-tournaments': {
    title: 'Crypto Gaming Tournaments — Win USDC Playing Games | EZGAME',
    h1:    'Crypto Gaming Tournaments',
    description: 'The fastest way to earn crypto playing games. $1 USDC entry. 30-minute match. 90% prize pool to the winner. Apex Legends and Fortnite on Polygon.',
    game:  'both',
    body: `
      <p>Crypto gaming in 2026 is no longer about minting tokens or breeding NFTs. It's about winning real money — paid in stablecoins — by being better than the other players in your pool.</p>
      <p>EZGAME runs Apex Legends and Fortnite kill tournaments on Polygon. $1 entry. 90% to the winner. Instant, on-chain, verifiable. No platform tokens to dump. Just USDC.</p>
    `,
    cta: { text: 'Play Now →', to: '/lobby' },
  },
  'play-to-earn-2026': {
    title: 'Play to Earn in 2026 — What Actually Works | EZGAME',
    h1:    'Play to Earn: What Actually Pays in 2026',
    description: 'P2E tokens died. Skill-based stablecoin tournaments are the new model. $1 entry, instant USDC payout, real games. Here\'s what play-to-earn looks like now.',
    game:  'both',
    body: `
      <p>Most play-to-earn projects from 2021–2023 are dead. Axie, Stepn, every yield-farm-with-a-game-skin — tokens went to zero because earnings depended on new player inflows, not gameplay skill.</p>
      <p>What works in 2026: <strong>win USDC by outplaying other people in real games</strong>. EZGAME is the thin end of this model. $1 entry, Fortnite or Apex, 30-minute match, and the best player walks away with 90% of the pool in stablecoins. No token. No inflation. Just skill.</p>
    `,
    cta: { text: 'Try a $1 Tournament →', to: '/lobby' },
  },
  'win-usdc-gaming': {
    title: 'Win USDC Playing Games — Instant On-Chain Payouts | EZGAME',
    h1:    'Win USDC by Playing Apex or Fortnite',
    description: 'The fastest USDC gaming payout on the market. $1 entry, one match, 90% prize pool to winner. Paid on Polygon in seconds.',
    game:  'both',
    body: `
      <p>If you're searching for a way to earn USDC by gaming, EZGAME is the most direct path. Enter a tournament for $1, play one ranked match in Apex Legends or Fortnite, and if you out-kill the pool, 90% of all entry fees hit your wallet instantly.</p>
      <p>No token swap. No bridge. No withdrawal queue. USDC on Polygon — spendable, transferable, or cashable to fiat through any major exchange.</p>
    `,
    cta: { text: 'Start Earning →', to: '/lobby' },
  },
  'polygon-gaming': {
    title: 'Gaming on Polygon — Fast, Cheap, Instant Payouts | EZGAME',
    h1:    'Polygon Gaming: Why the Best Esports Payouts Are On-Chain',
    description: 'Polygon delivers sub-second transactions with near-zero gas. EZGAME uses it to pay tournament winners instantly in USDC. Here\'s why Polygon is the best chain for gaming.',
    game:  'both',
    body: `
      <p>Polygon processes transactions in under 2 seconds with gas fees under $0.01. For gaming payouts, that means a tournament winner receives $90 in USDC within 10 seconds of verification. Players need a tiny amount of POL for gas — typically a fraction of a cent.</p>
      <p>Compared to Ethereum mainnet ($5–$30 gas, 12-second blocks) or Solana (frequent outages), Polygon is the most reliable, cheapest, and fastest chain for high-frequency small-value payouts. That's why EZGAME builds here.</p>
    `,
    cta: { text: 'Play on Polygon →', to: '/lobby' },
  },
  'fortnite-cash-cups-alternative': {
    title: 'The $1 Alternative to Fortnite Cash Cups | EZGAME',
    h1:    'A $1 Alternative to Fortnite Cash Cups',
    description: 'Cash Cups require Champion Division and region qualification. EZGAME tournaments require $1 and one match. Same game, faster payouts, zero barrier.',
    game:  'fortnite',
    body: `
      <p>Fortnite Cash Cups are great — if you're already in Champion Division, live in a competitive region, and can dedicate 3+ hours to a bracket format. For everyone else, there's been no way to earn money playing Fortnite.</p>
      <p>EZGAME changes that. $1 entry. One match. 30 minutes. Most eliminations wins 90% of the pool. No division requirement. No region lock. No bracket. Just play Fortnite and get paid if you're the best in the pool.</p>
    `,
    cta: { text: 'Enter a Tournament →', to: '/fortnite' },
  },
  'esports-instant-payout': {
    title: 'Esports With Instant Payout — No More Waiting Weeks | EZGAME',
    h1:    'Esports With Instant Payouts',
    description: 'Traditional esports takes 2-6 weeks to pay winners. EZGAME pays in seconds via smart contract. $1 entry, 90% to winner, USDC on Polygon.',
    game:  'both',
    body: `
      <p>The average esports tournament takes 2–6 weeks to process payouts. Some platforms take months. A few never pay at all. EZGAME's smart contract pays the winner the moment the match is verified — typically under 10 seconds after the window closes.</p>
      <p>No withdrawal form. No minimum balance. No PayPal. The contract sends USDC directly to your wallet, verifiable on Polygonscan. The transaction hash is your receipt.</p>
    `,
    cta: { text: 'Get Paid Instantly →', to: '/lobby' },
  },
  'one-dollar-gaming-tournament': {
    title: '$1 Gaming Tournament — Lowest Entry in Esports | EZGAME',
    h1:    '$1 Gaming Tournaments',
    description: 'The lowest entry fee in competitive gaming. $1 USDC, one match, 90% prize pool to the winner. Apex Legends and Fortnite.',
    game:  'both',
    body: `
      <p>Most cash tournament platforms charge $5–$25 per entry. EZGAME charges $1. That's the lowest-risk way to compete for real money in any game, period.</p>
      <p>At $1 per entry and $90 per win (with 100 players), the risk-reward ratio is 1:90. Compare that to a $10 entry platform paying $50 to the winner (1:5 ratio). EZGAME isn't just cheaper — it's structurally better value for the player.</p>
    `,
    cta: { text: 'Enter for $1 →', to: '/lobby' },
  },
  'metamask-gaming': {
    title: 'Gaming With MetaMask — How to Play & Earn With Your Wallet | EZGAME',
    h1:    'MetaMask Gaming: Connect, Play, Get Paid',
    description: 'Connect MetaMask to EZGAME, enter a $1 Apex or Fortnite tournament, and receive USDC payouts directly to your wallet. No signup, no KYC.',
    game:  'both',
    body: `
      <p>MetaMask is the only app you need to start earning money playing games on EZGAME. Install the extension, switch to Polygon network, fund with at least $1 USDC, and you're ready to enter tournaments.</p>
      <p>No email signup. No KYC. No personal information. Your wallet address is your identity. Payouts go straight to the same wallet — spendable, stakeable, or cashable to fiat through any major exchange.</p>
    `,
    cta: { text: 'Connect & Play →', to: '/lobby' },
  },
  'cs2-premier-tournaments': {
    title: 'CS2 Premier Kill Tournaments — Win USDC for Most Kills | EZGAME',
    h1:    'CS2 Premier Kill Tournaments',
    description: 'Enter CS2 Premier kill tournaments for $1 USDC. Most kills in a 60-minute window wins 90% of the prize pool — paid instantly on Polygon. No qualifiers.',
    game:  'cs2',
    body: `
      <p>EZGAME brings skill-based crypto payouts to Counter-Strike 2. Enter a CS2 Premier kill tournament for $1 USDC, rack up kills in a 60-minute window, and the player with the most frags wins 90% of the total prize pool — paid directly to your Polygon wallet in seconds.</p>
      <p>Kills are verified through the Steam API. Your Steam profile and CS2 match stats must be set to Public before entering — private stats cannot be verified. Tiebreaker is K/D ratio, then in-game score. No bracket. No elimination rounds. One Premier match. Best player wins.</p>
    `,
    cta: { text: 'Enter a CS2 Tournament →', to: '/cs2' },
  },
};

const PAGE_KEYS = Object.keys(PAGES);

export default function SeoPage() {
  const { slug } = useParams();
  const page = PAGES[slug];

  if (!page) return <Navigate to="/" replace />;

  const gameLabel = page.game === 'fortnite' ? 'Fortnite' : page.game === 'apex' ? 'Apex Legends' : page.game === 'cs2' ? 'CS2 Premier' : 'Apex & Fortnite';
  const accentColor = page.game === 'fortnite' ? '#FFB800' : page.game === 'cs2' ? '#FF6B00' : '#00D4FF';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.ezegames.live/' },
      { '@type': 'ListItem', position: 2, name: page.h1, item: `https://www.ezegames.live/${slug}` },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'How much does it cost to enter?', acceptedAnswer: { '@type': 'Answer', text: '$1 USDC entry. You also need a small amount of POL for gas — typically under $0.01 on Polygon.' } },
      { '@type': 'Question', name: 'How fast are payouts?', acceptedAnswer: { '@type': 'Answer', text: 'Instant. The smart contract pays the winner within seconds of match verification — no withdrawal queue.' } },
      { '@type': 'Question', name: 'What wallet do I need?', acceptedAnswer: { '@type': 'Answer', text: 'MetaMask or any WalletConnect-compatible wallet funded with USDC on Polygon network.' } },
      { '@type': 'Question', name: 'Which games are supported?', acceptedAnswer: { '@type': 'Answer', text: 'Apex Legends, Fortnite, and CS2 Premier are all supported. Each has dedicated lobbies with kill-based winner determination.' } },
    ],
  };

  return (
    <div className="seo-page">
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.description} />
        <link rel="canonical" href={`https://www.ezegames.live/${slug}`} />
        <meta property="og:type"        content="website" />
        <meta property="og:site_name"   content="EZGAME" />
        <meta property="og:locale"      content="en_US" />
        <meta property="og:title"       content={page.title} />
        <meta property="og:description" content={page.description} />
        <meta property="og:url"         content={`https://www.ezegames.live/${slug}`} />
        <meta property="og:image"       content="https://www.ezegames.live/og-image.png" />
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:site"        content="@EZ_Egames" />
        <meta name="twitter:title"       content={page.title} />
        <meta name="twitter:description" content={page.description} />
        <meta name="twitter:image"       content="https://www.ezegames.live/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Breadcrumb */}
      <nav className="seo-page__breadcrumb">
        <Link to="/">EzEgames</Link>
        <span> › </span>
        <span>{page.h1}</span>
      </nav>

      <h1 className="seo-page__h1">{page.h1}</h1>
      <p className="seo-page__sub">{page.description}</p>

      <div
        className="seo-page__body"
        dangerouslySetInnerHTML={{ __html: page.body }}
      />

      {/* CTA */}
      <div className="seo-page__cta-box" style={{ borderColor: `${accentColor}44` }}>
        <div className="seo-page__cta-game">{gameLabel}</div>
        <p className="seo-page__cta-copy">$1 USDC entry · 30-min match · 90% payout · Instant on Polygon</p>
        <Link to={page.cta.to} className="seo-page__cta-btn" style={{ background: accentColor }}>
          {page.cta.text}
        </Link>
      </div>

      {/* FAQ */}
      <div className="seo-page__faq">
        <h2>Frequently Asked Questions</h2>
        <details>
          <summary>How much does it cost to enter?</summary>
          <p>$1 USDC entry. You also need a small amount of POL in your wallet for gas — typically under $0.01 on Polygon. You can swap $0.10 USDC → POL directly from the join form.</p>
        </details>
        <details>
          <summary>How fast are payouts?</summary>
          <p>Instant. The smart contract pays the winner within seconds of match verification.</p>
        </details>
        <details>
          <summary>What wallet do I need?</summary>
          <p>MetaMask or any WalletConnect-compatible wallet funded with USDC on Polygon.</p>
        </details>
        <details>
          <summary>Is it available worldwide?</summary>
          <p>Yes. No region lock, no bank account required.</p>
        </details>
      </div>

      {/* Internal links to other SEO pages */}
      <div className="seo-page__related">
        <h3>Related</h3>
        <div className="seo-page__related-grid">
          {PAGE_KEYS.filter(k => k !== slug).slice(0, 4).map(k => (
            <Link key={k} to={`/${k}`} className="seo-page__related-card">
              {PAGES[k].h1}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export { PAGE_KEYS };
